/**
 * 1on1 タイムラインカード（メンバー詳細 / マイページ共通）
 *
 * 1 人のメンバーは上長・リーダー候補など複数のリーダーとそれぞれペアを持つ。
 * - 見えるペアが複数 → リーダーごとのタブで切り替える（1 つならタブは出さない）
 * - ペアあり → 「今週の 1on1 を開始」（Meet を開く）+ 週ごとのセッション一覧
 * - 自分がリーダーとして開設できる → 「1on1 を開設」（他のリーダーのペアがあっても出る）
 * - 見えるペアが無く開設もできない → 何も描画しない（`showPlaceholder` なら未開設カード）
 *
 * 「どのペアを見せるか/開設させるか」の判定は GET /one-on-one/pairs のサーバー応答
 * （`pairs` / `canOpen`）に委ねる。クライアント側で `canOpenPairFor`（team/group スコープ）や
 * 閲覧範囲を再実装すると、判定がずれるたびに新しい誤検知を生むため（開設ボタンが出るのに
 * 開設できない／participant なのにタイムラインが出ない、の両方が起こり得る）、権限ロジックの
 * 単一の情報源をサーバーに保つ
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business 1on1 の Meet 起動ボタンと週次タイムライン（Gemini 要約・合意アクション・原文リンク）をリーダーごとのタブで表示するカード
 * @graph-connects /one-on-one/pairs [calls] ペア取得・開設可否 API
 * @graph-connects /one-on-one/pairs/sessions [calls] タイムライン取得 API
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import type { OneOnOnePairResponse } from '@cortex/domain-product-innovation-lab-types';
import { useAuth } from '../../../hooks/useAuth.js';
import { useOneOnOnePair, useOneOnOneSessions } from '../../../hooks/useOneOnOne.js';
import { OneOnOneParticipantsModal } from './OneOnOneParticipantsModal.js';
import { OneOnOneSessionItem } from './OneOnOneSessionItem.js';

/** @graph-connects none */
interface OneOnOneTimelineCardProps {
  /** 対象メンバー */
  email: string;
  /**
   * ペアが無く開設もできないときに「未開設」の空状態カードを描く（専用タブに置くときに
   * 何も出ないと壊れて見えるため）。省略時は何も描画しない
   */
  showPlaceholder?: boolean;
}

/**
 * 参加者モーダルを、新規開設と既存ペアの共有相手編集のどちらで開いているか
 * @graph-connects none
 */
type ModalMode = 'open' | 'edit' | null;

/**
 * タブに出すリーダー名。ニックネームが引けなければメールのローカル部
 * @graph-connects none
 */
function leaderLabelOf(pair: OneOnOnePairResponse): string {
  return pair.ownerNickName ?? pair.ownerEmail.split('@')[0] ?? pair.ownerEmail;
}

/**
 * Meet を新しいタブで開く
 * @graph-connects none
 */
function openMeet(meetingUri: string): void {
  window.open(meetingUri, '_blank', 'noopener,noreferrer');
}

/** @graph-connects none */
export function OneOnOneTimelineCard({
  email,
  showPlaceholder = false,
}: OneOnOneTimelineCardProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedPairId, setSelectedPairId] = useState<string | null>(null);

  const viewerEmail = (user?.email ?? '').toLowerCase();
  const memberEmail = email.toLowerCase();

  const pairQuery = useOneOnOnePair(memberEmail);
  const pairs = pairQuery.data?.pairs ?? [];
  const canOpen = pairQuery.data?.canOpen ?? false;
  // 選んだペアが（共有解除などで）一覧から消えたら先頭に戻す
  const pair = pairs.find((p) => p.pairId === selectedPairId) ?? pairs[0] ?? null;
  const sessionsQuery = useOneOnOneSessions(pair?.pairId ?? null);

  if (pairQuery.isLoading) {
    return null;
  }

  // 管理（共有相手の変更・要約の修正・リーダー向けフィードバック）はペアのリーダー本人か admin。
  // サーバーの canManagePair と同じ条件で、ボタンを出すかどうかにだけ使う（判定自体はサーバー）
  const isAdmin = (user?.portalRoles ?? []).includes('admin');
  const canManage = pair !== null && (isAdmin || pair.ownerEmail.toLowerCase() === viewerEmail);

  if (!pair && !canOpen) {
    if (!showPlaceholder) {
      return null;
    }
    return (
      <Card className="animate-content-reveal" data-testid="one-on-one-timeline-card">
        <CardHeader title={t('oneOnOne.card.title')} description={t('oneOnOne.card.notOpened')} />
        <CardContent>
          <p className="text-sm text-[var(--color-text-muted)]">{t('oneOnOne.card.unavailable')}</p>
        </CardContent>
      </Card>
    );
  }

  const sessions = sessionsQuery.data?.flatMap((page) => page.sessions) ?? [];

  return (
    <Card className="animate-content-reveal" data-testid="one-on-one-timeline-card">
      <CardHeader
        title={t('oneOnOne.card.title')}
        description={pair ? t('oneOnOne.card.description') : t('oneOnOne.card.notOpened')}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {canManage && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setModalMode('edit');
                }}
                aria-label={t('oneOnOne.card.editParticipants')}
              >
                {t('oneOnOne.card.editParticipants')}
              </Button>
            )}
            {canOpen && (
              <Button
                size="sm"
                variant={pair ? 'secondary' : 'primary'}
                onClick={() => {
                  setModalMode('open');
                }}
                aria-label={t('oneOnOne.card.open')}
              >
                {t('oneOnOne.card.open')}
              </Button>
            )}
            {pair && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  openMeet(pair.meetingUri);
                }}
                aria-label={t('oneOnOne.card.startMeet')}
              >
                {t('oneOnOne.card.startMeet')}
              </Button>
            )}
          </div>
        }
      />
      <CardContent>
        {pairs.length > 1 && (
          <div
            className="mb-3 flex flex-wrap border-b border-[var(--color-border)]"
            role="tablist"
            aria-label={t('oneOnOne.card.leaderTabs')}
          >
            {pairs.map((p) => (
              <button
                key={p.pairId}
                type="button"
                role="tab"
                aria-selected={p.pairId === pair?.pairId}
                onClick={() => {
                  setSelectedPairId(p.pairId);
                }}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  p.pairId === pair?.pairId
                    ? 'text-[var(--color-accent-primary)] border-b-2 border-[var(--color-accent-primary)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                {t('oneOnOne.card.withLeader', { name: leaderLabelOf(p) })}
              </button>
            ))}
          </div>
        )}
        {pair && (
          <p className="mb-3 text-xs text-[var(--color-text-muted)]">
            {t('oneOnOne.card.participants', { participants: pair.participants.join(', ') })}
          </p>
        )}
        {pair && sessionsQuery.isLoading && (
          <p className="text-sm text-[var(--color-text-secondary)]">{t('oneOnOne.card.loading')}</p>
        )}
        {pair && sessionsQuery.isError && (
          <p role="alert" className="text-sm text-[var(--color-error)]">
            {t('oneOnOne.errors.loadFailed')}
          </p>
        )}
        {pair && sessionsQuery.isSuccess && sessions.length === 0 && (
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('oneOnOne.card.noSessions')}
          </p>
        )}
        {pair && sessions.length > 0 && (
          <ul className="space-y-3" aria-label={t('oneOnOne.card.title')}>
            {sessions.map((session) => (
              <OneOnOneSessionItem
                key={session.sessionId}
                pairId={pair.pairId}
                session={session}
                canManage={canManage}
              />
            ))}
          </ul>
        )}
        {pair && sessionsQuery.hasNextPage && (
          <div className="mt-3 flex justify-center">
            <Button
              size="sm"
              variant="secondary"
              loading={sessionsQuery.isFetchingNextPage}
              onClick={() => {
                void sessionsQuery.fetchNextPage();
              }}
            >
              {t('oneOnOne.card.loadMore')}
            </Button>
          </div>
        )}
      </CardContent>
      {modalMode !== null && (
        <OneOnOneParticipantsModal
          open
          onClose={() => {
            setModalMode(null);
          }}
          memberEmail={memberEmail}
          pairId={modalMode === 'edit' && pair ? pair.pairId : null}
          initialParticipants={modalMode === 'edit' && pair ? pair.participants : []}
          fixedEmails={
            modalMode === 'edit' && pair
              ? [pair.memberEmail, pair.ownerEmail]
              : [memberEmail, viewerEmail]
          }
        />
      )}
    </Card>
  );
}
